import * as React from 'react';
import { Box, Button, Grid, TextField, Typography } from '@mui/material';
import ClickAwayListener from '@mui/core/ClickAwayListener';
import { Business } from '@mui/icons-material';
import { TwitterPicker } from 'react-color';

import { AvatarSetting } from 'components';
import { useCompanyFormManager } from './company-form.hook';

interface CompanyFormProps {
  visibleSlug?: boolean;
  onSubmitting?: (loading: boolean) => void;
  next?: (data?: any) => void;
}

export const CompanyForm = React.forwardRef(
  ({ visibleSlug = false, onSubmitting = () => {}, next }: CompanyFormProps, ref) => {
    const [showPrimaryColorPicker, setShowPrimaryColorPicker] = React.useState<boolean>(false);
    const [showSecondaryColorPicker, setShowSecondaryColorPicker] = React.useState<boolean>(false);
    const [{ org, formManager, loading_submit }] = useCompanyFormManager({ next });

    React.useImperativeHandle(ref, () => ({ onSubmit: () => formManager.handleSubmit() }), []);
    React.useEffect(() => onSubmitting(loading_submit), [loading_submit]);

    return (
      <form onSubmit={formManager.handleSubmit} autoComplete="off">
        <Box paddingY={2}>
          <Grid container rowSpacing={3} columnSpacing={2}>
            <Grid item xs={12}>
              <Box marginBottom={1} display="flex" alignItems="center">
                <Typography variant="caption" color="textSecondary">
                  Customize your Cleancode® decal
                </Typography>
              </Box>

              <AvatarSetting
                avatarUrl={formManager.values.avatar ? URL.createObjectURL(formManager.values.avatar) : org?.logo?.url}
                isError={!!(formManager.errors.avatar && formManager.touched.avatar)}
                helperText={formManager.touched.avatar && formManager.errors.avatar ? formManager.errors.avatar : ''}
                placeholderImage={
                  <Business
                    sx={{
                      fontSize: 64,
                    }}
                  />
                }
                onSelectFile={(file) => formManager.setFieldValue('avatar', file)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Company name"
                variant="outlined"
                fullWidth
                value={formManager.values.name}
                onChange={formManager.handleChange('name')}
                error={!!(formManager.errors.name && formManager.touched.name)}
                helperText={formManager.touched.name ? formManager.errors.name : null}
              />
            </Grid>

            {visibleSlug && (
              <Grid item xs={12}>
                <TextField label="API Url Slug" variant="outlined" fullWidth value={formManager.values.slug} disabled />
              </Grid>
            )}

            <Grid item xs={12}>
              <Box marginBottom={1} display="flex" alignItems="center">
                <Typography variant="caption">Add at least one brand color</Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Primary Color
                  </Typography>
                  <ClickAwayListener
                    onClickAway={() => {
                      setShowPrimaryColorPicker(false);
                    }}
                  >
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowPrimaryColorPicker(!showPrimaryColorPicker);
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Box
                            width={16}
                            height={16}
                            bgcolor={formManager.values.primary_brand_color}
                            marginRight={2}
                            borderRadius={0.5}
                          />
                          <Typography color="textSecondary">{formManager.values.primary_brand_color}</Typography>
                        </Box>
                      </Button>

                      <Box
                        sx={{
                          display: showPrimaryColorPicker ? 'block' : 'none',
                          position: 'absolute',
                          zIndex: 1,
                          marginTop: 0.5,
                          marginLeft: 0.4,
                        }}
                      >
                        <TwitterPicker
                          color={formManager.values.primary_brand_color}
                          onChangeComplete={(color) => {
                            formManager.setFieldValue('primary_brand_color', color.hex);
                          }}
                        />
                      </Box>
                    </Box>
                  </ClickAwayListener>
                </Box>

                <Box
                  sx={{
                    marginLeft: 2,
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Secondary Color
                  </Typography>
                  <ClickAwayListener
                    onClickAway={() => {
                      setShowSecondaryColorPicker(false);
                    }}
                  >
                    <Box>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          setShowSecondaryColorPicker(!showSecondaryColorPicker);
                        }}
                      >
                        <Box display="flex" alignItems="center" justifyContent="center">
                          <Box
                            width={16}
                            height={16}
                            bgcolor={formManager.values.secondary_brand_color}
                            marginRight={2}
                            borderRadius={0.5}
                          />
                          <Typography color="textSecondary">{formManager.values.secondary_brand_color}</Typography>
                        </Box>
                      </Button>

                      <Box
                        sx={{
                          display: showSecondaryColorPicker ? 'block' : 'none',
                          position: 'absolute',
                          zIndex: 1,
                          marginTop: 0.5,
                          marginLeft: 0.4,
                        }}
                      >
                        <TwitterPicker
                          color={formManager.values.secondary_brand_color}
                          onChangeComplete={(color) => {
                            formManager.setFieldValue('secondary_brand_color', color.hex);
                          }}
                        />
                      </Box>
                    </Box>
                  </ClickAwayListener>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </form>
    );
  },
);
